import {
  Add as AddIcon,
  Business as BusinessIcon,
  Close as CloseIcon,
  ContentCopy as ContentCopyIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  MoreVert as MoreVertIcon,
  PeopleOutline as PeopleIcon,
  PersonOutline as PersonIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import {
  Alert,
  Autocomplete,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  List,
  ListItem,
  ListItemAvatar,
  ListItemIcon,
  ListItemSecondaryAction,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import axios from "axios";
import moment from "moment";
import React, { useEffect, useState } from "react";
import LoadingButton from "../../components/LoadingButton";
import { hostname, parseError } from "../../utils";

interface Company {
  id: string;
  name: string | null;
  uid: string | null;
  ownerEmail: string | null;
  ownerName: string | null;
  memberCount: number;
  creditLimit: number | null;
  creditUsage: number;
  zeroDataRetention: boolean;
  mfaRequired: boolean;
  createdDate: string | null;
}

interface UserOption {
  id: string;
  email: string;
  displayName: string | null;
  photo: string | null;
}

interface CompanyUser {
  id: string;
  companyId: string;
  uid: string | null;
  email: string;
  role: string;
  displayName: string | null;
  photo: string | null;
  createdDate: string | null;
}

interface CompanyManagementProps {
  getToken: () => Promise<string>;
}

const ROLE_OPTIONS = [
  { value: "owner", label: "Owner" },
  { value: "admin", label: "Admin" },
  { value: "member", label: "Member" },
];

const CompanyManagement: React.FC<CompanyManagementProps> = ({ getToken }) => {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);

  // Search
  const [searchQuery, setSearchQuery] = useState("");
  const [searchTimeout, setSearchTimeout] =
    useState<NodeJS.Timeout | null>(null);

  // Copy ID feedback
  const [copiedId, setCopiedId] = useState<string | null>(null);

  // Menu
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [menuCompany, setMenuCompany] = useState<Company | null>(null);

  // Edit dialog
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingCompany, setEditingCompany] = useState<Company | null>(null);
  const [saving, setSaving] = useState(false);
  const [editForm, setEditForm] = useState({
    name: "",
    uid: null as string | null,
    creditLimit: null as number | null,
    zeroDataRetention: false,
    mfaRequired: false,
  });

  // Owner search
  const [ownerSearchQuery, setOwnerSearchQuery] = useState("");
  const [ownerOptions, setOwnerOptions] = useState<UserOption[]>([]);
  const [ownerSearchLoading, setOwnerSearchLoading] = useState(false);
  const [selectedOwner, setSelectedOwner] = useState<UserOption | null>(null);
  const [ownerSearchTimeout, setOwnerSearchTimeout] =
    useState<NodeJS.Timeout | null>(null);

  // Members dialog
  const [membersDialogOpen, setMembersDialogOpen] = useState(false);
  const [membersCompany, setMembersCompany] = useState<Company | null>(null);
  const [companyUsers, setCompanyUsers] = useState<CompanyUser[]>([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [addMemberEmail, setAddMemberEmail] = useState("");
  const [addMemberRole, setAddMemberRole] = useState("member");
  const [addingMember, setAddingMember] = useState(false);
  const [editingMember, setEditingMember] = useState<CompanyUser | null>(null);
  const [editMemberRole, setEditMemberRole] = useState("");
  const [savingMember, setSavingMember] = useState(false);
  const [deletingMemberId, setDeletingMemberId] = useState<string | null>(null);

  const fetchCompanies = async (search?: string) => {
    setLoading(true);
    setError(null);

    try {
      const token = await getToken();
      const params: Record<string, string | number> = {
        page: page + 1,
        perPage: rowsPerPage,
      };

      if (search) {
        params.search = search;
      }

      const response = await axios.get(`${hostname}/companies/admin`, {
        params,
        headers: { Authorization: `Bearer ${token}` },
      });

      setCompanies(response.data.companies);
      setTotalCount(response.data.pagination.total);
    } catch (err) {
      setError(parseError(err));
    } finally {
      setLoading(false);
    }
  };

  const fetchCompanyUsers = async (companyId: string) => {
    setLoadingMembers(true);
    try {
      const token = await getToken();
      const response = await axios.get(
        `${hostname}/companies/admin/${companyId}/users`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setCompanyUsers(response.data.users || []);
    } catch (err) {
      setError(parseError(err));
    } finally {
      setLoadingMembers(false);
    }
  };

  const searchUsers = async (query: string) => {
    if (!query || query.length < 2) {
      setOwnerOptions([]);
      return;
    }

    setOwnerSearchLoading(true);
    try {
      const token = await getToken();
      const response = await axios.get(`${hostname}/users/search`, {
        params: { query },
        headers: { Authorization: `Bearer ${token}` },
      });
      setOwnerOptions(response.data.users || []);
    } catch (err) {
      console.error("Failed to search users:", err);
      setOwnerOptions([]);
    } finally {
      setOwnerSearchLoading(false);
    }
  };

  useEffect(() => {
    fetchCompanies(searchQuery);
  }, [page, rowsPerPage]);

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);

    // Debounce search
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    const timeout = setTimeout(() => {
      setPage(0);
      fetchCompanies(value);
    }, 300);

    setSearchTimeout(timeout);
  };

  const handleOwnerSearchChange = (value: string) => {
    setOwnerSearchQuery(value);

    // Debounce search
    if (ownerSearchTimeout) {
      clearTimeout(ownerSearchTimeout);
    }

    const timeout = setTimeout(() => {
      searchUsers(value);
    }, 300);

    setOwnerSearchTimeout(timeout);
  };

  // Menu handlers
  const handleMenuOpen = (
    event: React.MouseEvent<HTMLElement>,
    company: Company
  ) => {
    setMenuAnchorEl(event.currentTarget);
    setMenuCompany(company);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setMenuCompany(null);
  };

  const handleEditCompany = (company: Company) => {
    setEditingCompany(company);
    setEditForm({
      name: company.name || "",
      uid: company.uid,
      creditLimit: company.creditLimit,
      zeroDataRetention: company.zeroDataRetention,
      mfaRequired: company.mfaRequired,
    });
    // Set the current owner as selected
    if (company.uid) {
      setSelectedOwner({
        id: company.uid,
        email: company.ownerEmail || "",
        displayName: company.ownerName,
        photo: null,
      });
    } else {
      setSelectedOwner(null);
    }
    setOwnerOptions([]);
    setOwnerSearchQuery("");
    setEditDialogOpen(true);
    handleMenuClose();
  };

  const handleSaveCompany = async () => {
    if (!editingCompany) return;

    setSaving(true);
    setError(null);

    try {
      const token = await getToken();
      await axios.put(
        `${hostname}/companies/admin/${editingCompany.id}`,
        editForm,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      setSuccess("Company updated successfully");
      setEditDialogOpen(false);
      fetchCompanies(searchQuery);
    } catch (err) {
      setError(parseError(err));
    } finally {
      setSaving(false);
    }
  };

  // Members dialog handlers
  const handleManageMembers = (company: Company) => {
    setMembersCompany(company);
    setMembersDialogOpen(true);
    setAddMemberEmail("");
    setAddMemberRole("member");
    setEditingMember(null);
    fetchCompanyUsers(company.id);
    handleMenuClose();
  };

  const handleCloseMembersDialog = () => {
    setMembersDialogOpen(false);
    setMembersCompany(null);
    setCompanyUsers([]);
    setEditingMember(null);
    // Refresh the company list to update member counts
    fetchCompanies(searchQuery);
  };

  const handleAddMember = async () => {
    if (!membersCompany || !addMemberEmail.trim()) return;

    setAddingMember(true);
    setError(null);

    try {
      const token = await getToken();
      await axios.post(
        `${hostname}/companies/admin/${membersCompany.id}/users`,
        {
          email: addMemberEmail.trim(),
          role: addMemberRole,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      setSuccess("Member added successfully");
      setAddMemberEmail("");
      setAddMemberRole("member");
      fetchCompanyUsers(membersCompany.id);
    } catch (err) {
      setError(parseError(err));
    } finally {
      setAddingMember(false);
    }
  };

  const handleEditMember = (user: CompanyUser) => {
    setEditingMember(user);
    setEditMemberRole(user.role);
  };

  const handleSaveMember = async () => {
    if (!membersCompany || !editingMember) return;

    setSavingMember(true);
    setError(null);

    try {
      const token = await getToken();
      await axios.put(
        `${hostname}/companies/admin/${membersCompany.id}/users/${editingMember.id}`,
        { role: editMemberRole },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      setSuccess("Member updated successfully");
      setEditingMember(null);
      fetchCompanyUsers(membersCompany.id);
    } catch (err) {
      setError(parseError(err));
    } finally {
      setSavingMember(false);
    }
  };

  const handleDeleteMember = async (userId: string) => {
    if (!membersCompany) return;

    setDeletingMemberId(userId);
    setError(null);

    try {
      const token = await getToken();
      await axios.delete(
        `${hostname}/companies/admin/${membersCompany.id}/users/${userId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      setSuccess("Member removed successfully");
      fetchCompanyUsers(membersCompany.id);
    } catch (err) {
      setError(parseError(err));
    } finally {
      setDeletingMemberId(null);
    }
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "Unknown";
    return moment(dateString).format("MMM D, YYYY");
  };

  const handleCopyId = (id: string) => {
    navigator.clipboard.writeText(id);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case "owner":
        return "primary";
      case "admin":
        return "info";
      default:
        return "default";
    }
  };

  return (
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 600, mb: 3 }}>
        Company Management
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert
          severity="success"
          sx={{ mb: 2 }}
          onClose={() => setSuccess(null)}
        >
          {success}
        </Alert>
      )}

      {/* Search */}
      <Box sx={{ mb: 3 }}>
        <TextField
          placeholder="Search by company name, ID, or owner UID..."
          value={searchQuery}
          onChange={(e) => handleSearchChange(e.target.value)}
          size="small"
          sx={{ width: 400 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {/* Companies Table */}
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Company</TableCell>
                <TableCell>Owner</TableCell>
                <TableCell>Members</TableCell>
                <TableCell>Credits</TableCell>
                <TableCell>Settings</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {companies.map((company) => (
                <TableRow key={company.id} hover>
                  <TableCell>
                    <Tooltip
                      title={
                        copiedId === company.id ? "Copied!" : "Click to copy"
                      }
                      placement="top"
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                          cursor: "pointer",
                          "&:hover": { color: "primary.main" },
                        }}
                        onClick={() => handleCopyId(company.id)}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            fontFamily: "monospace",
                            maxWidth: 80,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {company.id}
                        </Typography>
                        <ContentCopyIcon sx={{ fontSize: 14, opacity: 0.6 }} />
                      </Box>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <BusinessIcon color="action" />
                      <Typography variant="body2" fontWeight={500}>
                        {company.name || "Unnamed Company"}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="body2" fontWeight={500}>
                        {company.ownerName || "No owner"}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {company.ownerEmail || company.uid}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={company.memberCount}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    {company.creditLimit !== null ? (
                      <Typography variant="body2">
                        {company.creditUsage.toLocaleString()} /{" "}
                        {company.creditLimit.toLocaleString()}
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No limit
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", gap: 0.5 }}>
                      {company.zeroDataRetention && (
                        <Chip label="ZDR" size="small" color="warning" />
                      )}
                      {company.mfaRequired && (
                        <Chip label="MFA" size="small" color="info" />
                      )}
                      {!company.zeroDataRetention && !company.mfaRequired && (
                        <Typography variant="body2" color="text.secondary">
                          -
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {formatDate(company.createdDate)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={(e) => handleMenuOpen(e, company)}
                    >
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}

              {companies.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      No companies found
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[10, 25, 50, 100]}
            component="div"
            count={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </TableContainer>
      )}

      {/* Actions Menu */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => menuCompany && handleEditCompany(menuCompany)}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit Company</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => menuCompany && handleManageMembers(menuCompany)}
        >
          <ListItemIcon>
            <PeopleIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Manage Members</ListItemText>
        </MenuItem>
      </Menu>

      {/* Edit Company Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            Edit Company
            <IconButton onClick={() => setEditDialogOpen(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {editingCompany && (
            <Box sx={{ pt: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Company ID: {editingCompany.id}
              </Typography>

              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Company Name"
                    value={editForm.name}
                    onChange={(e) =>
                      setEditForm({ ...editForm, name: e.target.value })
                    }
                  />
                </Grid>

                <Grid item xs={12}>
                  <Autocomplete
                    value={selectedOwner}
                    onChange={(event, newValue) => {
                      setSelectedOwner(newValue);
                      setEditForm({
                        ...editForm,
                        uid: newValue?.id || null,
                      });
                    }}
                    inputValue={ownerSearchQuery}
                    onInputChange={(event, newInputValue, reason) => {
                      if (reason === "input") {
                        handleOwnerSearchChange(newInputValue);
                      }
                    }}
                    options={ownerOptions}
                    loading={ownerSearchLoading}
                    getOptionLabel={(option) =>
                      option.displayName || option.email || option.id
                    }
                    isOptionEqualToValue={(option, value) =>
                      option.id === value.id
                    }
                    filterOptions={(x) => x}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Owner"
                        placeholder="Search by email or name..."
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {ownerSearchLoading ? (
                                <CircularProgress color="inherit" size={20} />
                              ) : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                        helperText="Search for a user to transfer ownership"
                      />
                    )}
                    renderOption={(props, option) => (
                      <li {...props} key={option.id}>
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 2 }}
                        >
                          <Avatar
                            src={option.photo || undefined}
                            sx={{ width: 32, height: 32 }}
                          >
                            {option.displayName?.[0] ||
                              option.email?.[0] || <PersonIcon />}
                          </Avatar>
                          <Box>
                            <Typography variant="body2" fontWeight={500}>
                              {option.displayName || "No name"}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {option.email}
                            </Typography>
                          </Box>
                        </Box>
                      </li>
                    )}
                    noOptionsText={
                      ownerSearchQuery.length < 2
                        ? "Type at least 2 characters to search"
                        : "No users found"
                    }
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Credit Limit"
                    type="number"
                    value={editForm.creditLimit ?? ""}
                    onChange={(e) =>
                      setEditForm({
                        ...editForm,
                        creditLimit: e.target.value
                          ? parseInt(e.target.value)
                          : null,
                      })
                    }
                    helperText="Leave empty for no company-level credit limit"
                  />
                </Grid>

                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={editForm.zeroDataRetention}
                        onChange={(e) =>
                          setEditForm({
                            ...editForm,
                            zeroDataRetention: e.target.checked,
                          })
                        }
                      />
                    }
                    label="Zero Data Retention (ZDR)"
                  />
                  <Typography
                    variant="caption"
                    display="block"
                    color="text.secondary"
                  >
                    When enabled, conversation data is not stored
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={editForm.mfaRequired}
                        onChange={(e) =>
                          setEditForm({
                            ...editForm,
                            mfaRequired: e.target.checked,
                          })
                        }
                      />
                    }
                    label="MFA Required"
                  />
                  <Typography
                    variant="caption"
                    display="block"
                    color="text.secondary"
                  >
                    When enabled, all users must use MFA to access the company
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <LoadingButton onClick={() => setEditDialogOpen(false)}>
            Cancel
          </LoadingButton>
          <LoadingButton
            variant="contained"
            onClick={handleSaveCompany}
            loading={saving}
          >
            Save Changes
          </LoadingButton>
        </DialogActions>
      </Dialog>

      {/* Manage Members Dialog */}
      <Dialog
        open={membersDialogOpen}
        onClose={handleCloseMembersDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Box>
              Manage Members
              {membersCompany && (
                <Typography variant="body2" color="text.secondary">
                  {membersCompany.name || "Unnamed Company"}
                </Typography>
              )}
            </Box>
            <IconButton onClick={handleCloseMembersDialog} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {/* Add Member Form */}
          <Box sx={{ mb: 3, mt: 1 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Add Member
            </Typography>
            <Box sx={{ display: "flex", gap: 1 }}>
              <TextField
                size="small"
                placeholder="Email address"
                value={addMemberEmail}
                onChange={(e) => setAddMemberEmail(e.target.value)}
                sx={{ flex: 1 }}
              />
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <Select
                  value={addMemberRole}
                  onChange={(e) => setAddMemberRole(e.target.value)}
                >
                  {ROLE_OPTIONS.map((role) => (
                    <MenuItem key={role.value} value={role.value}>
                      {role.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <LoadingButton
                variant="contained"
                size="small"
                onClick={handleAddMember}
                loading={addingMember}
                disabled={!addMemberEmail.trim()}
                startIcon={<AddIcon />}
              >
                Add
              </LoadingButton>
            </Box>
          </Box>

          {/* Members List */}
          {loadingMembers ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <List sx={{ pt: 0 }}>
              {companyUsers.map((user) => (
                <ListItem
                  key={user.id}
                  sx={{
                    borderRadius: 1,
                    mb: 1,
                    backgroundColor: "action.hover",
                  }}
                >
                  <ListItemAvatar>
                    <Avatar src={user.photo || undefined}>
                      {user.displayName?.[0] ||
                        user.email?.[0]?.toUpperCase() || <PersonIcon />}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Typography variant="body2" fontWeight={500}>
                          {user.displayName || user.email}
                        </Typography>
                        {editingMember?.id === user.id ? (
                          <FormControl size="small" sx={{ minWidth: 100 }}>
                            <Select
                              value={editMemberRole}
                              onChange={(e) => setEditMemberRole(e.target.value)}
                              size="small"
                            >
                              {ROLE_OPTIONS.map((role) => (
                                <MenuItem key={role.value} value={role.value}>
                                  {role.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        ) : (
                          <Chip
                            label={user.role}
                            size="small"
                            color={getRoleColor(user.role) as any}
                          />
                        )}
                      </Box>
                    }
                    secondary={
                      user.displayName ? user.email : formatDate(user.createdDate)
                    }
                  />
                  <ListItemSecondaryAction>
                    {editingMember?.id === user.id ? (
                      <Box sx={{ display: "flex", gap: 0.5 }}>
                        <LoadingButton
                          size="small"
                          onClick={handleSaveMember}
                          loading={savingMember}
                        >
                          Save
                        </LoadingButton>
                        <Button
                          size="small"
                          onClick={() => setEditingMember(null)}
                        >
                          Cancel
                        </Button>
                      </Box>
                    ) : (
                      <Box sx={{ display: "flex", gap: 0.5 }}>
                        <IconButton
                          size="small"
                          onClick={() => handleEditMember(user)}
                          title="Edit role"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteMember(user.id)}
                          disabled={deletingMemberId === user.id}
                          title="Remove member"
                          color="error"
                        >
                          {deletingMemberId === user.id ? (
                            <CircularProgress size={16} />
                          ) : (
                            <DeleteIcon fontSize="small" />
                          )}
                        </IconButton>
                      </Box>
                    )}
                  </ListItemSecondaryAction>
                </ListItem>
              ))}

              {companyUsers.length === 0 && (
                <Typography
                  color="text.secondary"
                  sx={{ textAlign: "center", py: 4 }}
                >
                  No members found
                </Typography>
              )}
            </List>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={handleCloseMembersDialog}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CompanyManagement;
